import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft,
  CreditCard,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  FileText,
  Download
} from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'

const CustomerLoans = () => {
  const navigate = useNavigate()
  const { customer } = useCustomerAuth()
  const [loanData, setLoanData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedLoan, setSelectedLoan] = useState(null)
  const [showPayments, setShowPayments] = useState({})
  const [showSchedule, setShowSchedule] = useState({})

  useEffect(() => {
    fetchLoans()
  }, [])

  const fetchLoans = async () => {
    try {
      setLoading(true)
      setError('')
      
      const response = await apiService.getCustomerLoans()
      
      if (response.success) {
        setLoanData(response.data)
      } else {
        setError(response.message || 'Failed to fetch loan data')
      }
    } catch (error) {
      console.error('Error fetching loans:', error)
      setError('Failed to load loan information')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    if (!amount) return 'UGX 0'
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-GB')
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'active':
      case 'disbursed':
        return 'text-green-600 bg-green-100'
      case 'pending':
        return 'text-yellow-600 bg-yellow-100'
      case 'rejected':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const togglePayments = (loanId) => {
    setShowPayments(prev => ({
      ...prev,
      [loanId]: !prev[loanId]
    }))
  }

  const toggleSchedule = (loanId) => {
    setShowSchedule(prev => ({
      ...prev,
      [loanId]: !prev[loanId]
    }))
  }

  const calculateLoanSummary = () => {
    if (!loanData?.loanApplications) return null

    const totalLoaned = loanData.loanApplications.reduce((sum, loan) => sum + (loan.loanAmountApproved || 0), 0)
    const activeLoans = loanData.loanApplications.filter(loan => 
      ['approved', 'active', 'disbursed'].includes(loan.loanStatus?.toLowerCase())
    ).length
    const totalPayments = loanData.loanApplications.reduce((sum, loan) => 
      sum + (loan.payments || []).reduce((paySum, payment) => paySum + (payment.paymentAmount || 0), 0), 0
    )

    return {
      totalLoaned,
      activeLoans,
      totalPayments,
      outstandingBalance: totalLoaned - totalPayments
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-gray-300 rounded"></div>
              ))}
            </div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-gray-300 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/customer/dashboard')}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </button>
          
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Loading Loans</h3>
                <p className="mt-2 text-sm text-red-700">{error}</p>
                <button
                  onClick={fetchLoans}
                  className="mt-3 text-sm bg-red-100 text-red-800 px-3 py-1 rounded hover:bg-red-200"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const summary = calculateLoanSummary()

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/customer/dashboard')}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Loans</h1>
              <p className="mt-2 text-gray-600">
                View and manage your loan applications and payment history
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <DollarSign className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Loaned</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(summary.totalLoaned)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <CreditCard className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Loans</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.activeLoans}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Payments</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(summary.totalPayments)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Outstanding</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(summary.outstandingBalance)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loan Applications */}
        <div className="space-y-6">
          {!loanData?.loanApplications || loanData.loanApplications.length === 0 ? (
            <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Loans Found</h3>
              <p className="text-gray-600">You haven't applied for any loans yet.</p>
            </div>
          ) : (
            loanData.loanApplications.map((loan) => (
              <div key={loan.applicationId} className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Loan Application #{loan.applicationId}
                      </h3>
                      <p className="text-sm text-gray-600">{loan.loanProductName}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(loan.loanStatus)}`}>
                        {loan.loanStatus}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div>
                      <p className="text-sm text-gray-600">Loan Amount</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {formatCurrency(loan.loanAmountApproved)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Interest Rate</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {loan.interestRate}% per month
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Term</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {loan.loanTermMonths} months
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <p className="text-sm text-gray-600">Application Date</p>
                      <p className="text-sm text-gray-900">{formatDate(loan.applicationDate)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Purpose</p>
                      <p className="text-sm text-gray-900">{loan.loanPurpose || 'Not specified'}</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    {loan.payments && loan.payments.length > 0 && (
                      <button
                        onClick={() => togglePayments(loan.applicationId)}
                        className="flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100"
                      >
                        <DollarSign className="h-4 w-4 mr-2" />
                        {showPayments[loan.applicationId] ? 'Hide' : 'Show'} Payments ({loan.payments.length})
                      </button>
                    )}
                    
                    {loan.repaymentSchedule && loan.repaymentSchedule.length > 0 && (
                      <button
                        onClick={() => toggleSchedule(loan.applicationId)}
                        className="flex items-center px-4 py-2 bg-green-50 text-green-700 rounded-md hover:bg-green-100"
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        {showSchedule[loan.applicationId] ? 'Hide' : 'Show'} Schedule ({loan.repaymentSchedule.length})
                      </button>
                    )}
                  </div>

                  {/* Payment History */}
                  {showPayments[loan.applicationId] && loan.payments && (
                    <div className="mt-6 border-t pt-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Payment History</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Principal
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Interest
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Method
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {loan.payments.map((payment) => (
                              <tr key={payment.paymentId}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatDate(payment.paymentDate)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {formatCurrency(payment.paymentAmount)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatCurrency(payment.principalAmount)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatCurrency(payment.interestAmount)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {payment.paymentMethod}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Repayment Schedule */}
                  {showSchedule[loan.applicationId] && loan.repaymentSchedule && (
                    <div className="mt-6 border-t pt-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Repayment Schedule</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Due Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Principal
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Interest
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Total Due
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {loan.repaymentSchedule.map((schedule) => (
                              <tr key={schedule.scheduleId}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatDate(schedule.dueDate)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatCurrency(schedule.principalDue)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatCurrency(schedule.interestDue)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {formatCurrency(schedule.totalDue)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(schedule.status)}`}>
                                    {schedule.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default CustomerLoans
